import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ActivityItemEdge,
	is1PActivity,
	isDocumentActivity,
	isGraphActivity,
	isRemoteLinkActivity,
	recentActivitiesQuery,
} from '@atlassian/search-client';
import { type ExperimentConfig } from '@atlassian/search-experiment';

export const INCLUDED_PRODUCTS = ['confluence', 'jira', 'townsquare', 'graph'];

export const INCLUDED_OBJECT_TYPES = [
	'page',
	'blogpost',
	'whiteboard',
	'issue',
	'project',
	'goal',
	'content',
	'document',
	'remote-link',
];

/**
 * Returns the list of products to be included in the recent activities query based on the experiment.
 * We do NOT manipulate INCLUDED_OBJECT_TYPES to keep the logic simple.
 *
 * https://switcheroo.atlassian.com/ui/gates/0cd2bdec-6ec9-49e4-9393-54e282cf472a/key/remove-jira-recents-from-quick-find-in-confluence
 */
const getIncludedProductsBasedOnCohort = (): string[] => {
	const COHORTS = {
		removeJira: 'remove-jira', // Test 1.
		confluenceOnly: 'confluence-only', // Test 2.
		control: 'control', // Control
	};
	const removeJiraRecentsExperimentCohort = FeatureGates.getExperimentValue<string>(
		'remove-jira-recents-from-quick-find-in-confluence',
		'cohort',
		'control',

		// We need to know the cohort to load the correct products, but we are NOT triggering the exposure
		// event yet since it would be too wide. Instead, we're doing that in src/ui/quick-find/dialog/index.tsx.
		// For the decision see https://hello.atlassian.net/wiki/spaces/SEARCH/pages/5615125165/Experiment+structure#Options.
		{ fireExperimentExposure: false },
	);

	if (removeJiraRecentsExperimentCohort === COHORTS.removeJira) {
		return INCLUDED_PRODUCTS.filter((product) => product !== 'jira');
	} else if (removeJiraRecentsExperimentCohort === COHORTS.confluenceOnly) {
		return ['confluence'];
	}

	return INCLUDED_PRODUCTS;
};

export const fetchRecentActivities = async (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	experimentConfig?: ExperimentConfig,
	after: string = '',
) => {
	const includedProductsBasedOnCohort = getIncludedProductsBasedOnCohort();

	const recentActivitiesProductsFromCombinedExperiment =
		experimentConfig?.combinedExperiment?.enabled &&
		experimentConfig?.combinedExperiment?.uiFeatures?.recentActivitiesProducts;

	const overrideFilteredProducts =
		Array.isArray(recentActivitiesProductsFromCombinedExperiment) &&
		recentActivitiesProductsFromCombinedExperiment.every((p) => INCLUDED_PRODUCTS.includes(p))
			? recentActivitiesProductsFromCombinedExperiment
			: undefined;

	return await recentActivitiesQuery({
		variables: {
			filteredProducts: overrideFilteredProducts || includedProductsBasedOnCohort,
			filteredObjectTypes: INCLUDED_OBJECT_TYPES,
			filteredRootContainerIds: [
				`ari:cloud:platform::site/${cloudId}`,
				`ari:cloud:townsquare::site/${cloudId}`,
			],
			itemsLimit: 200,
			after,
			includeJiraIssueTypeAvatar: fg('rovo_search_jira_icons'),
			includeJiraIssueMetadata: fg('rovo_qf_jira_recents_metadata'),
		},
		aggAbsoluteUrl,
	}).then((response) => {
		const edges = response.data?.activity?.myActivity?.all?.edges || [];
		return edges.map(mapEssentialData);
	});
};

export const mapEssentialData = (result: ActivityItemEdge) => {
	const product = result.node.object.product;
	let title = '';
	const timestamp = result.node?.event?.timestamp;

	if (is1PActivity(result.node.object)) {
		if (product === 'confluence') {
			title = result.node.object.data?.title || '';
		} else if (product === 'jira') {
			const key = result.node.object.data?.key;
			const summary = result.node.object.data?.fieldsById?.edges?.[0]?.node?.text;
			title = key && summary ? `${key}: ${summary}` : '';
		} else if (product === 'townsquare') {
			title = result.node.object.data?.name || '';
		}
	} else if (
		isGraphActivity(result.node.object) &&
		(isDocumentActivity(result.node.object) || isRemoteLinkActivity(result.node.object))
	) {
		title = result.node.object.data?.displayName || '';
	} else {
		title = '';
	}

	return {
		id: result.node.object.id,
		title,
		data: result,
		type: 'recent-activity' as const,
		activityTimestamp: timestamp,
	};
};
